import React from "react";
import { Search, Filter, Download, X } from "lucide-react";
import { Payment } from "../../../utils/database";
import TransactionItem from "../items/TransactionItem";

interface FilterState {
  search: string;
  status: string;
  operator: string;
  provider: string;
  dateRange: string;
  startDate: string;
  endDate: string;
}

interface TransactionsListProps {
  transactions: Payment[];
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  onTransactionAction: (action: string, transaction: Payment, data?: any) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  startIndex: number;
  endIndex: number;
  totalTransactions: number;
  onExportAll?: () => void;
  loading?: boolean;
}

const TransactionsList: React.FC<TransactionsListProps> = ({
  transactions,
  filters,
  onFiltersChange,
  onTransactionAction,
  currentPage,
  totalPages,
  onPageChange,
  startIndex,
  endIndex,
  totalTransactions,
  onExportAll,
  loading,
}) => {
  // Transactions are already paginated server-side, no client-side slicing needed
  const currentTransactions = transactions;

  // Extraire les valeurs uniques pour les filtres
  const uniqueStatuses = [...new Set(transactions.map((t) => t.status))];
  const uniqueOperators = [...new Set(transactions.map((t) => t.operator))];
  const uniqueProviders = [...new Set(transactions.map((t) => t.provider))];

  const hasActiveFilters =
    filters.search ||
    filters.status ||
    filters.operator ||
    filters.provider ||
    filters.dateRange ||
    filters.startDate ||
    filters.endDate;

  const clearFilters = () => {
    onFiltersChange({
      search: "",
      status: "",
      operator: "",
      provider: "",
      dateRange: "",
      startDate: "",
      endDate: "",
    });
  };

  return (
    <div className="space-y-6">
      {/* Filtres et Recherche */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        {/* Header Filtres */}
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-600" />
            Filtres
          </h3>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-2 px-3 py-1 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
              Réinitialiser
            </button>
          )}
        </div>

        <div className="space-y-4">
          {/* Row 1: Recherche et Filtres */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            {/* Recherche */}
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase">Recherche</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="ID, téléphone, email..."
                  value={filters.search}
                  onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
                  className="pl-10 pr-4 py-2.5 w-full border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium"
                />
              </div>
            </div>

            {/* Filtre Statut */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase">Statut</label>
              <select
                value={filters.status}
                onChange={(e) => onFiltersChange({ ...filters, status: e.target.value })}
                className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium bg-white"
              >
                <option value="">Tous</option>
                {uniqueStatuses.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtre Opérateur */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase">Opérateur</label>
              <select
                value={filters.operator}
                onChange={(e) => onFiltersChange({ ...filters, operator: e.target.value })}
                className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium bg-white"
              >
                <option value="">Tous</option>
                {uniqueOperators.map((operator) => (
                  <option key={operator} value={operator}>
                    {operator}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtre Provider */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase">Provider</label>
              <select
                value={filters.provider}
                onChange={(e) => onFiltersChange({ ...filters, provider: e.target.value })}
                className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium bg-white"
              >
                <option value="">Tous</option>
                {uniqueProviders.map((provider) => (
                  <option key={provider} value={provider}>
                    {provider}
                  </option>
                ))}
              </select>
            </div>

            {/* Bouton Export */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase">Action</label>
              <button
                onClick={onExportAll}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2.5 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all font-semibold flex items-center justify-center gap-2 border-2 border-blue-700"
              >
                <Download className="w-4 h-4" />
                Exporter
              </button>
            </div>
          </div>

          {/* Row 2: Filtres par intervalle de dates */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase">Date début</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => onFiltersChange({ ...filters, startDate: e.target.value })}
                className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase">Date fin</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => onFiltersChange({ ...filters, endDate: e.target.value })}
                className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase">Période prédéfinie</label>
              <select
                value={filters.dateRange}
                onChange={(e) => onFiltersChange({ ...filters, dateRange: e.target.value })}
                className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium bg-white"
              >
                <option value="">Personnalisée</option>
                <option value="today">Aujourd'hui</option>
                <option value="yesterday">Hier</option>
                <option value="last7days">7 derniers jours</option>
                <option value="last30days">30 derniers jours</option>
                <option value="thisMonth">Ce mois</option>
                <option value="lastMonth">Mois dernier</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Résumé */}
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-gray-700">
          Affichage <span className="text-blue-600">{currentTransactions.length}</span> transaction(s) sur{" "}
          <span className="text-blue-600">{totalTransactions}</span>
        </div>
      </div>

      {/* Transactions - Layout Carte */}
      <div className="space-y-3 relative">
        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-70 z-10 flex items-center justify-center rounded-xl">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        )}
        {currentTransactions.length > 0 ? (
          currentTransactions.map((transaction) => (
            <TransactionItem key={transaction.id} transaction={transaction} onAction={onTransactionAction} />
          ))
        ) : (
          <div className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-12 text-center">
            <div className="flex flex-col items-center space-y-3">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                <Filter className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-lg font-bold text-gray-900">Aucune transaction trouvée</p>
              <p className="text-sm text-gray-600">Essayez de modifier vos critères de recherche ou vos filtres</p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-sm"
                >
                  Réinitialiser les filtres
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm font-semibold text-gray-700">
              Affichage {startIndex + 1}-{Math.min(endIndex, totalTransactions)} sur {totalTransactions} transactions
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border-2 border-gray-300 rounded-lg text-sm font-semibold text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                ← Précédent
              </button>
              <div className="flex items-center gap-2">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => onPageChange(pageNum)}
                      className={`w-10 h-10 rounded-lg font-semibold transition-colors ${
                        pageNum === currentPage
                          ? "bg-blue-600 text-white"
                          : "border-2 border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border-2 border-gray-300 rounded-lg text-sm font-semibold text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                Suivant →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionsList;
